// Frontend mirror of backend Person precedence helpers.
// Pure structural types — usable from any Person-shaped object
// (LinkedPerson.person, PersonListItem, PersonDetail, etc.).
// CRM-side overrides win when non-null; otherwise fall back to the
// Blast-mirrored canonical field.

type WithCrmFirstName = { crmFirstName: string | null; firstName: string };
type WithCrmLastName = { crmLastName: string | null; lastName: string };
type WithCrmEmail = { crmEmail?: string | null; email?: string | null };
type WithCrmPhone = { crmPhone?: string | null; phone?: string | null };

export const effectiveFirstName = (p: WithCrmFirstName): string =>
  p.crmFirstName ?? p.firstName;

export const effectiveLastName = (p: WithCrmLastName): string =>
  p.crmLastName ?? p.lastName;

export const effectiveEmail = (p: WithCrmEmail): string | null =>
  p.crmEmail ?? p.email ?? null;

export const effectivePhone = (p: WithCrmPhone): string | null =>
  p.crmPhone ?? p.phone ?? null;

export interface CallableNumber {
  /** null = the only number to show; render no tag. */
  label: 'Preferred' | 'Blast' | null;
  /** Raw stored value — used for the tel: href and run through formatPhone() for display. */
  value: string;
}

/** Reduce a phone-ish string to its last 10 digits — used ONLY to decide whether
 *  two values are the same number. Never used for display. */
const normDigits = (s: string | null | undefined): string =>
  String(s ?? '').replace(/\D/g, '').slice(-10);

/**
 * The distinct callable numbers for a person, preferred first.
 * - crmPhone + phone present and different → [Preferred (crmPhone), Blast (phone)]
 * - both present but the same number, or only one present → one untagged entry
 * - neither → []
 * Equality is by last-10-digits; the returned `value` stays raw.
 */
export const callableNumbers = (p: WithCrmPhone): CallableNumber[] => {
  const crm = p.crmPhone?.trim() || null;
  const blast = p.phone?.trim() || null;
  if (crm && blast) {
    if (normDigits(crm) === normDigits(blast)) return [{ label: null, value: crm }];
    return [
      { label: 'Preferred', value: crm },
      { label: 'Blast', value: blast },
    ];
  }
  if (crm) return [{ label: null, value: crm }];
  if (blast) return [{ label: null, value: blast }];
  return [];
};
